"use client";

import React from "react";
import { CircleCheck as CheckCircle2, ShieldCheck, Hammer, ArrowUpRight, Compass } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
export default function Intro() {
  const shouldReduceMotion = useReducedMotion();
  const metrics = [{
    icon: ShieldCheck,
    title: "Structural plans",
    desc: "Architectural stamped designs and turnkey Brookhaven permitting."
  }, {
    icon: Hammer,
    title: "Custom finish carpentry",
    desc: "Tongue-and-groove ceilings, bespoke millwork, and curated masonry."
  }];
  return <section id="herointro" data-nav="light" className="relative bg-background text-foreground overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28 lg:pt-40 lg:pb-32">
      {/* Decorative architectural background grid accents */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[linear-gradient(to_right,#111827_1px,transparent_1px),linear-gradient(to_bottom,#111827_1px,transparent_1px)] bg-[size:4rem_4rem]" />

      <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
          
          {/* Left Column: Narrative & Action (5 cols on lg) */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 flex flex-col justify-center space-y-6">
            {/* Eyebrow Badge */}
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-primary/10 border-primary/30 text-primary-foreground font-semibold px-3.5 py-1 text-xs tracking-wider uppercase rounded-full shadow-2xs">
                <Compass className="size-3.5 mr-1.5 text-primary" />
                Brookhaven, GA Craftsmanship
              </Badge>
            </div>

            {/* Exact Required Headline */}
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] font-semibold leading-[1.15] tracking-tight text-foreground">
              Home Additions, Basement &amp; Outdoor Living Remodeling in Brookhaven, GA
            </h1>

            {/* Architectural Gold Hairline Divider */}
            <div className="w-24 h-0.5 bg-primary/60 rounded-full" />

            {/* Authoritative Lead Copy */}
            <p className="font-sans text-base sm:text-lg text-foreground/85 leading-relaxed">
              Expand your home with custom architectural additions, light-filled daylight basements, and covered outdoor living spaces built for year-round entertaining across Brookhaven and North Atlanta.
            </p>

            {/* Primary & Secondary Call to Actions */}
            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground font-semibold px-8 py-3.5 rounded-lg border border-primary/80 shadow-md hover:bg-primary/90 transition-[background-color,box-shadow] duration-200">
                <Link to="/ContactRequestAnEstimatePage">
                  Request an Estimate
                  <ArrowUpRight className="size-4 ml-1.5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border bg-card/60 backdrop-blur-xs text-foreground font-medium px-6 py-3.5 rounded-lg hover:border-primary/50 hover:bg-accent/40 transition-[background-color,border-color] duration-200">
                <Link to="/OurProjectsPage">
                  View Our Projects
                </Link>
              </Button>
            </div>

            {/* Trust Assurance Bar */}
            <div className="pt-4 flex items-center text-xs text-foreground/75">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Brookhaven Permitting Managed</span>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Architectural Photography & Atelier Plinth (7 cols on lg) */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.12,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-7 relative">
            {/* Visual Frame */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-border/80 bg-card aspect-[4/3] sm:aspect-[16/11]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_addition.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_addition.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_addition.png-wvc-sizes" alt="Custom covered outdoor living porch and finished terrace addition in Brookhaven GA" loading="eager" decoding="async" className="w-full h-full object-cover object-center" />
              {/* Subtle inner protective vignette */}
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/50 via-transparent to-transparent pointer-events-none" />

              {/* Verified Local Tag Top Right */}
              <div className="absolute top-4 right-4 bg-secondary/85 text-secondary-foreground backdrop-blur-md px-3.5 py-1.5 rounded-full text-xs font-medium tracking-wide flex items-center gap-1.5 shadow-sm border border-white/10">
                <span className="size-2 rounded-full bg-primary" />
                <span>Historic Brookhaven Residence</span>
              </div>
            </div>

            {/* Atelier Frosted Glass Plinth Card Overlay */}
            <motion.div animate={shouldReduceMotion ? {} : {
            y: [0, -6, 0]
          }} transition={shouldReduceMotion ? {} : {
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }} className="mt-4 sm:mt-0 sm:absolute sm:-bottom-6 sm:left-6 sm:right-6 lg:left-8 lg:-bottom-8 lg:right-8 bg-card/90 backdrop-blur-md p-5 sm:p-6 rounded-2xl border border-white/80 shadow-xl">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                {metrics.map((item, index) => {
                const Icon = item.icon;
                return <div key={item.title} data-index={index} className="flex-1 flex items-start gap-3.5">
                      <div className="size-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0 border border-primary/20">
                        <Icon className="size-5" />
                      </div>
                      <div className="space-y-0.5">
                        <p className="text-sm font-semibold text-foreground">
                          {item.title}
                        </p>
                        <p className="text-xs text-foreground/75 leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                      {index === 0 && <Separator orientation="vertical" className="hidden sm:block h-10 self-center ml-2 bg-border/80" />}
                    </div>;
              })}
              </div>
            </motion.div>
          </motion.div>

        </div>
      </div>
    </section>;
}